import { skipToken } from "@reduxjs/toolkit/query";
import { AlertCircle, ArrowLeft, ClipboardList, Sparkles } from "lucide-react";
import * as React from "react";
import { FormattedNumber } from "react-intl";
import { Link, Navigate, useParams } from "react-router-dom";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Checkbox,
  Label,
  Progress,
  Switch,
  Textarea,
  useChat,
  useToast,
} from "randmarcomps";
import {
  OrderDetail as ApiOrderDetail,
  useGetV4ManufacturerByRouteManufacturerIdDocumentsActiveOrderDetailsQuery,
  useGetV4ManufacturerByRouteManufacturerIdOpportunityAndBidNumberQuery,
  usePostV4ManufacturerByRouteManufacturerIdOpportunityAndBidNumberFieldNameMutation,
} from "randmar-api-client";
import { IOpportunity } from "../api/opportunity/OpportunitiesApi";
import BidResellersGrid from "../components/grids/BidResellersGrid";
import OpenOrdersGrid from "../components/grids/OpenOrdersGrid";
import OpportunityDetailsGrid from "../components/grids/OpportunityDetailsGrid";
import OpportunityActions from "../components/opportunities/OpportunityActions";
import CurrencyUtils from "../components/util/CurrencyUtils";
import { DataRefreshBar } from "../components/util/DataRefreshBar";
import { FormatDateFromString, FormatDateFromStringWithHours } from "../components/util/DateTimeUtil";
import InlineSpinner from "../components/util/InlineSpinner";
import { useManufacturer } from "../contexts/WithManufacturer";
import Path from "../routes/Path";
import { OrderDetail } from "../stores/document/Document";
import { Opportunity } from "../stores/opportunities/Opportunity";
import withRoot from "../withRoot";

const parseOpportunityDateOrNull = (dateValue?: string | null) => {
  if (!dateValue) return null;

  if (/^\\d{8}$/.test(dateValue)) {
    const year = Number.parseInt(dateValue.slice(0, 4), 10);
    const month = Number.parseInt(dateValue.slice(4, 6), 10);
    const day = Number.parseInt(dateValue.slice(6, 8), 10);
    const parsed = new Date(year, month - 1, day);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const parsed = new Date(dateValue);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const hasEndDateInFuture = (endDate?: string | null) => {
  const parsedEndDate = parseOpportunityDateOrNull(endDate);
  if (!parsedEndDate) return false;
  return parsedEndDate >= new Date();
};

const isOpportunityActive = (opportunity: Opportunity) => Boolean(opportunity.active) && hasEndDateInFuture(opportunity.endDate);

const defaultResellerOptions = [
  { value: "Exclusive", label: "Exclusive", description: "Qualified resellers in the Exclusive package." },
  { value: "E-Commerce", label: "E‑Commerce", description: "Qualified resellers in the E‑Commerce package." },
  { value: "Commercial", label: "Commercial", description: "Qualified resellers in the Commercial package." },
  { value: "Academy", label: "Academy", description: "Qualified resellers in the Academy buying group." },
  { value: "Novexco", label: "Novexco", description: "Qualified resellers in the Novexco buying group." },
  { value: "COOPSCO", label: "COOPSCO", description: "Qualified resellers in the COOPSCO buying group." },
  { value: "Jean Coutu", label: "Jean Coutu", description: "Qualified resellers in the Jean Coutu buying group." },
  { value: "Starter", label: "Starter", description: "Qualified resellers in the Starter package." },
] as const;

const toggleCommaSeparated = (currentValue: string, token: string) => {
  const normalized = currentValue || "";
  if (normalized.includes(token)) {
    return normalized.replaceAll(`${token},`, "");
  }
  return `${normalized}${token},`;
};

function buildOpportunityAssistantPrompt(options: {
  applicationId: string;
  bidNumber: string;
  opportunityName: string;
  isActive: boolean;
  endDateLabel: string;
  defaultResellersValue: string;
  resellersCount: number;
  partNumbersCount: number;
  openOrdersCount: number;
}) {
  return [
    "You are assisting with the Randmar Manufacturer Portal Opportunity page.",
    `Manufacturer applicationId: ${options.applicationId}`,
    `Opportunity: ${options.bidNumber} — ${options.opportunityName}`,
    `Status: ${options.isActive ? "Active" : "Ended"}`,
    `End date: ${options.endDateLabel}`,
    `Default resellers: ${options.defaultResellersValue || "None"}`,
    `Resellers linked: ${options.resellersCount}`,
    `Part numbers in bid: ${options.partNumbersCount}`,
    `Open orders for this opportunity: ${options.openOrdersCount}`,
    "",
    "Help me interpret what I'm seeing and suggest next actions (reseller outreach, pricing adjustments, timing, and cleanup).",
  ].join("\\n");
}

const OpportunityPage: React.FC = () => {
  const { applicationId } = useManufacturer();
  const { toast } = useToast();
  const { sendMessageWithClear } = useChat();
  const { bidNumber: bidNumberParam } = useParams<{ bidNumber: string }>();

  const bidNumber = (bidNumberParam || "").trim();
  const opportunityQueryArg = applicationId && bidNumber ? { routeManufacturerId: applicationId, bidNumber } : skipToken;
  const openOrdersQueryArg = applicationId ? { routeManufacturerId: applicationId } : skipToken;

  const opportunityQuery = useGetV4ManufacturerByRouteManufacturerIdOpportunityAndBidNumberQuery(opportunityQueryArg);
  const openOrdersQuery = useGetV4ManufacturerByRouteManufacturerIdDocumentsActiveOrderDetailsQuery(openOrdersQueryArg);

  const [editOpportunityField, editOpportunityFieldState] =
    usePostV4ManufacturerByRouteManufacturerIdOpportunityAndBidNumberFieldNameMutation();

  const [lastRefreshedAt, setLastRefreshedAt] = React.useState<Date | null>(null);
  const isRefreshing = Boolean(opportunityQuery.isFetching || openOrdersQuery.isFetching);

  const opportunity = React.useMemo(() => {
    const raw = opportunityQuery.data as unknown;
    if (!raw) return null;
    if (Array.isArray(raw)) {
      if (raw.length === 0) return null;
      return Opportunity.convertFrom(raw[0] as unknown as IOpportunity);
    }

    try {
      return Opportunity.convertFrom(raw as unknown as IOpportunity);
    } catch (error) {
      console.error("Failed to normalize opportunity response:", error);
      return null;
    }
  }, [opportunityQuery.data]);

  const openOrders = React.useMemo(() => {
    const raw = openOrdersQuery.data as ApiOrderDetail[] | undefined;
    if (!raw) return [];
    return raw.map(OrderDetail.convertFromApi);
  }, [openOrdersQuery.data]);

  const opportunityOpenOrders = React.useMemo(
    () => openOrders.filter((openOrder) => openOrder.opportunityManufacturerNumber === bidNumber),
    [bidNumber, openOrders]
  );

  const resellersCount = opportunity?.resellers?.length ?? 0;
  const partNumbersCount = opportunity?.details?.length ?? 0;

  React.useEffect(() => {
    if (isRefreshing) return;
    if (opportunityQuery.data === undefined && openOrdersQuery.data === undefined && !opportunityQuery.isError && !openOrdersQuery.isError) {
      return;
    }
    setLastRefreshedAt(new Date());
  }, [isRefreshing, opportunityQuery.data, opportunityQuery.isError, openOrdersQuery.data, openOrdersQuery.isError]);

  const previousTitleRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    previousTitleRef.current = document.title;
    return () => {
      if (previousTitleRef.current) {
        document.title = previousTitleRef.current;
      }
    };
  }, []);

  React.useEffect(() => {
    if (!previousTitleRef.current) return;
    if (!bidNumber) return;

    const name = opportunity?.opportunity || opportunity?.bidNumber || "Opportunity";
    document.title = `Opportunity ${bidNumber} — ${name} | ${previousTitleRef.current}`;
  }, [bidNumber, opportunity?.bidNumber, opportunity?.opportunity]);

  const [defaultResellersDraft, setDefaultResellersDraft] = React.useState("");
  const [descriptionDraft, setDescriptionDraft] = React.useState("");
  const [isEditingDescription, setIsEditingDescription] = React.useState(false);

  React.useEffect(() => {
    if (!opportunity) return;
    setDefaultResellersDraft(opportunity.defaultResellers || "");
    setDescriptionDraft(opportunity.description || "");
    setIsEditingDescription(false);
  }, [opportunity?.bidNumber]);

  const handleRefresh = React.useCallback(async () => {
    await Promise.all([opportunityQuery.refetch(), openOrdersQuery.refetch()]);
  }, [openOrdersQuery, opportunityQuery]);

  const isActive = opportunity ? isOpportunityActive(opportunity) : false;

  const openOrdersStats = React.useMemo(() => {
    const orders = opportunityOpenOrders.filter((order) => order.documentType === "Order");
    const shipments = opportunityOpenOrders.filter((order) => order.documentType === "Shipment");

    const currentOrders = orders.filter((order) => order.warehouseCode !== "3PLE");
    const delayedOrders = orders.filter((order) => order.warehouseCode === "3PLE");

    const sum = (items: OrderDetail[]) => items.reduce((total, next) => total + (next.extendedPrice || 0), 0);

    return [
      { label: "Processing", count: currentOrders.length, amount: sum(currentOrders) },
      { label: "Shipped", count: shipments.length, amount: sum(shipments) },
      { label: "Delayed", count: delayedOrders.length, amount: sum(delayedOrders) },
    ];
  }, [opportunityOpenOrders]);

  const assistantPrompt = React.useMemo(() => {
    if (!applicationId || !bidNumber) return "";
    if (!opportunity) return "";

    const endDateLabel = FormatDateFromString(opportunity.endDate || undefined) || "—";
    return buildOpportunityAssistantPrompt({
      applicationId,
      bidNumber,
      opportunityName: opportunity.opportunity || "Untitled",
      isActive,
      endDateLabel,
      defaultResellersValue: opportunity.defaultResellers || "",
      resellersCount,
      partNumbersCount,
      openOrdersCount: opportunityOpenOrders.length,
    });
  }, [applicationId, bidNumber, isActive, opportunity, opportunityOpenOrders.length, partNumbersCount, resellersCount]);

  const pageContainerClassName = "mx-auto w-full max-w-[1440px] space-y-8 pb-10";
  const surfaceClassName = "rounded-2xl border border-gray-100 bg-white p-6 shadow-sm";

  if (!bidNumber) {
    return <Navigate to={Path.opportunities(applicationId)} replace />;
  }

  if (!opportunityQuery.isFetching && !opportunity && opportunityQuery.data !== undefined) {
    return <Navigate to={Path.opportunities(applicationId)} replace />;
  }

  const handleUpdateField = async (fieldName: string, nextValue: string) => {
    if (!applicationId || !opportunity?.bidNumber) return;

    try {
      await editOpportunityField({
        routeManufacturerId: applicationId,
        bidNumber: opportunity.bidNumber,
        fieldName,
        newValue: nextValue,
      }).unwrap();
      toast({ variant: "success", title: "Updated", description: `${fieldName} saved.` });
      opportunityQuery.refetch();
    } catch (error) {
      console.error(`Failed to update opportunity field ${fieldName}:`, error);
      toast({ variant: "destructive", title: "Error", description: `Could not update ${fieldName}.` });
    }
  };

  const handleSaveDefaultResellers = async () => {
    await handleUpdateField("DefaultResellers", defaultResellersDraft);
  };

  const handleSaveDescription = async () => {
    await handleUpdateField("Description", descriptionDraft);
    setIsEditingDescription(false);
  };

  return (
    <div className={pageContainerClassName} aria-busy={isRefreshing}>
      <header className={surfaceClassName} role="banner">
        <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
          <div className="space-y-2">
            <Link
              to={Path.opportunities(applicationId)}
              className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500 hover:text-[color:var(--ops-accent)]"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
              Opportunities
            </Link>

            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-2 rounded-full bg-[var(--ops-accent-muted)] px-3 py-1 text-xs font-semibold uppercase tracking-wide text-[var(--ops-accent)]">
                <Sparkles className="h-4 w-4" aria-hidden="true" />
                Manufacturer Portal
              </span>
              {opportunity ? (
                <Badge variant={isActive ? "success" : "secondary"} size="sm" className="w-fit">
                  {isActive ? "Active" : "Ended"}
                </Badge>
              ) : null}
              {isRefreshing ? (
                <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <InlineSpinner size="sm" decorative />
                  Syncing
                </span>
              ) : null}
            </div>

            <h1 className="text-[32px] font-semibold tracking-tight text-slate-900">{bidNumber}</h1>
            <p className="max-w-2xl text-[15px] text-slate-600">
              {opportunity?.opportunity || "Review bid details, pricing, and reseller participation."}
            </p>
            {isRefreshing ? <Progress className="mt-3 h-1" aria-label="Refreshing opportunity data" /> : null}
          </div>

          <div className="flex flex-col gap-3 md:items-end">
            <DataRefreshBar
              align="right"
              lastRefreshedAt={lastRefreshedAt}
              isRefreshing={isRefreshing}
              onRefresh={handleRefresh}
              actions={(
                <Button
                  type="button"
                  size="icon"
                  variant="outline"
                  onClick={() => assistantPrompt && sendMessageWithClear(assistantPrompt)}
                  className="rounded-xl"
                  aria-label="Open assistant with this page context"
                  disabled={!assistantPrompt}
                >
                  <Sparkles className="h-4 w-4" aria-hidden="true" />
                </Button>
              )}
            />

            {opportunity ? <OpportunityActions opportunity={opportunity} onUpdated={() => opportunityQuery.refetch()} /> : null}
          </div>
        </div>

        {opportunityQuery.isError ? (
          <Alert variant="destructive" className="mt-6 rounded-2xl border border-red-100 bg-white">
            <AlertTitle>Couldn’t load this opportunity</AlertTitle>
            <AlertDescription>Try refreshing this page.</AlertDescription>
          </Alert>
        ) : null}
      </header>

      <section className={surfaceClassName} aria-label="Overview">
        <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
          <div className="space-y-1">
            <h2 className="text-[24px] font-semibold text-slate-900">Overview</h2>
            <p className="text-[15px] text-slate-600">Core bid properties and default reseller targeting.</p>
          </div>
          {opportunity ? (
            <div className="flex flex-wrap gap-3">
              <div className="flex items-center gap-3 rounded-2xl border border-gray-100 bg-white px-4 py-3 shadow-sm">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Active</p>
                  <p className="text-[13px] text-slate-600">Enable or disable this bid</p>
                </div>
                <Switch
                  checked={Boolean(opportunity.active)}
                  onCheckedChange={(checked) => handleUpdateField("Active", checked ? "true" : "false")}
                  disabled={editOpportunityFieldState.isLoading}
                  aria-label="Toggle opportunity active state"
                />
              </div>
              <div className="flex items-center gap-3 rounded-2xl border border-gray-100 bg-white px-4 py-3 shadow-sm">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Default eligible</p>
                  <p className="text-[13px] text-slate-600">Allow resellers to set default</p>
                </div>
                <Switch
                  checked={Boolean(opportunity.canBeSetAsDefault)}
                  onCheckedChange={(checked) => handleUpdateField("CanBeSetAsDefault", checked ? "true" : "false")}
                  disabled={editOpportunityFieldState.isLoading}
                  aria-label="Toggle opportunity default eligibility"
                />
              </div>
            </div>
          ) : null}
        </div>

        {opportunity ? (
          <div className="mt-6 grid gap-4 md:grid-cols-3 lg:grid-cols-5">
            <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Start date</p>
              <p className="mt-1 text-[15px] font-medium text-slate-900">{FormatDateFromString(opportunity.startDate || undefined) || "—"}</p>
            </div>
            <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">End date</p>
              <p className="mt-1 text-[15px] font-medium text-slate-900">{FormatDateFromString(opportunity.endDate || undefined) || "—"}</p>
            </div>
            <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Resellers</p>
              <p className="mt-2 text-2xl font-semibold text-slate-900">{resellersCount}</p>
            </div>
            <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Part numbers</p>
              <p className="mt-2 text-2xl font-semibold text-slate-900">{partNumbersCount}</p>
            </div>
            <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Last update</p>
              <p className="mt-1 text-[15px] font-medium text-slate-900">
                {FormatDateFromStringWithHours(opportunity.lastUpdate || undefined) || FormatDateFromString(opportunity.lastUpdate || undefined) || "—"}
              </p>
            </div>
          </div>
        ) : null}

        <div className="mt-6 grid gap-4 lg:grid-cols-2">
          <Card className="rounded-2xl border border-gray-100 bg-white shadow-sm">
            <CardHeader>
              <CardTitle className="text-xl font-medium text-slate-900">Default resellers</CardTitle>
              <CardDescription className="text-[15px] text-slate-600">
                Selecting packages automatically targets all qualified resellers in those tiers.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-2">
                {defaultResellerOptions.map((option) => {
                  const id = `default-resellers-${option.value}`;
                  const checked = defaultResellersDraft.includes(`${option.value},`);
                  return (
                    <label
                      key={option.value}
                      htmlFor={id}
                      className="flex cursor-pointer items-start gap-3 rounded-2xl border border-gray-100 bg-white p-4 shadow-sm transition-colors hover:bg-gray-50"
                    >
                      <Checkbox
                        id={id}
                        checked={checked}
                        onCheckedChange={() => setDefaultResellersDraft((prev) => toggleCommaSeparated(prev, option.value))}
                        disabled={editOpportunityFieldState.isLoading}
                      />
                      <span className="space-y-1">
                        <span className="block text-[15px] font-medium text-slate-900">{option.label}</span>
                        <span className="block text-[13px] text-slate-500">{option.description}</span>
                      </span>
                    </label>
                  );
                })}
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-[13px] text-slate-500">
                  Current value: <span className="font-medium text-slate-700">{defaultResellersDraft || "None"}</span>
                </p>
                <Button
                  type="button"
                  variant="outline"
                  className="rounded-xl"
                  onClick={handleSaveDefaultResellers}
                  disabled={editOpportunityFieldState.isLoading || !opportunity}
                >
                  {editOpportunityFieldState.isLoading ? <InlineSpinner className="mr-2" decorative /> : null}
                  Save
                </Button>
              </div>

              {defaultResellersDraft && (opportunity?.resellers?.length ?? 0) === 0 ? (
                <Alert className="rounded-2xl border border-gray-100 bg-white">
                  <AlertTitle>Resellers will auto-populate</AlertTitle>
                  <AlertDescription>
                    Default resellers are enabled but no resellers are listed yet. Use Refresh and check again after processing completes.
                  </AlertDescription>
                </Alert>
              ) : null}
            </CardContent>
          </Card>

          <Card className="rounded-2xl border border-gray-100 bg-white shadow-sm">
            <CardHeader>
              <CardTitle className="text-xl font-medium text-slate-900">Description</CardTitle>
              <CardDescription className="text-[15px] text-slate-600">
                Add requirements like end-user constraints, minimums, or special timelines.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {isEditingDescription ? (
                <>
                  <Label htmlFor="opportunity-description" className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Opportunity description
                  </Label>
                  <Textarea
                    id="opportunity-description"
                    value={descriptionDraft}
                    onChange={(event) => setDescriptionDraft(event.target.value)}
                    className="min-h-[160px] resize-y rounded-xl border-slate-200 text-[15px]"
                    disabled={editOpportunityFieldState.isLoading}
                  />
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      type="button"
                      className="rounded-xl"
                      onClick={handleSaveDescription}
                      disabled={editOpportunityFieldState.isLoading || !opportunity}
                    >
                      {editOpportunityFieldState.isLoading ? <InlineSpinner className="mr-2" decorative /> : null}
                      Save
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      className="rounded-xl"
                      onClick={() => {
                        setDescriptionDraft(opportunity?.description || "");
                        setIsEditingDescription(false);
                      }}
                      disabled={editOpportunityFieldState.isLoading}
                    >
                      Cancel
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <p className="whitespace-pre-line text-[15px] text-slate-700">
                    {opportunity?.description || <span className="text-slate-500">No description yet.</span>}
                  </p>
                  <Button type="button" variant="outline" className="rounded-xl" onClick={() => setIsEditingDescription(true)} disabled={!opportunity}>
                    Edit description
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </section>

      {opportunityOpenOrders.length > 0 ? (
        <section className={surfaceClassName} aria-label="Open orders">
          <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div className="space-y-1">
              <h2 className="text-[24px] font-semibold text-slate-900">Open orders</h2>
              <p className="text-[15px] text-slate-600">Line items that reference this opportunity.</p>
            </div>
            <div className="inline-flex items-center gap-2 rounded-full bg-slate-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-slate-600">
              <ClipboardList className="h-4 w-4" aria-hidden="true" />
              {opportunityOpenOrders.length} lines
            </div>
          </div>

          <dl className="mt-6 grid gap-4 md:grid-cols-3" aria-label="Open order metrics">
            {openOrdersStats.map((stat) => (
              <div key={stat.label} className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
                <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">{stat.label}</dt>
                <dd className="mt-2 flex items-baseline justify-between gap-4">
                  <span className="text-2xl font-semibold text-slate-900">{stat.count}</span>
                  <span className="text-[15px] font-medium text-slate-700">
                    <FormattedNumber value={stat.amount} {...CurrencyUtils.format} />
                  </span>
                </dd>
              </div>
            ))}
          </dl>

          <div className="mt-6">
            <OpenOrdersGrid productOrders={opportunityOpenOrders} defaultRowsPerPage={20} withSort />
          </div>
        </section>
      ) : null}

      <section className={surfaceClassName} aria-label="Resellers">
        <div className="space-y-1">
          <h2 className="text-[24px] font-semibold text-slate-900">Resellers</h2>
          <p className="text-[15px] text-slate-600">Track invited partners and send pricing notifications.</p>
        </div>

        {opportunity?.resellers && opportunity.resellers.length > 0 ? (
          <div className="mt-6">
            <BidResellersGrid resellers={opportunity.resellers} bidNumber={opportunity.bidNumber || bidNumber} onChanged={() => opportunityQuery.refetch()} />
          </div>
        ) : (
          <Card className="mt-6 rounded-2xl border border-dashed border-gray-200 bg-white shadow-sm">
            <CardHeader>
              <CardTitle className="text-xl font-medium text-slate-900">No resellers added yet</CardTitle>
              <CardDescription className="text-[15px] text-slate-600">
                Search for a reseller, open Actions, and add them to this opportunity.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex items-start gap-3 text-[15px] text-slate-600">
              <AlertCircle className="mt-0.5 h-5 w-5 text-slate-400" aria-hidden="true" />
              <div className="space-y-1">
                <p>Tip: If you selected default reseller packages, refresh to check for auto-added partners.</p>
                <Link
                  to={Path.search(applicationId)}
                  className="inline-flex items-center gap-2 font-semibold text-[color:var(--ops-accent)] hover:underline"
                >
                  Search resellers
                  <ArrowLeft className="h-4 w-4 rotate-180" aria-hidden="true" />
                </Link>
              </div>
            </CardContent>
          </Card>
        )}
      </section>

      <section className={surfaceClassName} aria-label="Part numbers">
        <div className="space-y-1">
          <h2 className="text-[24px] font-semibold text-slate-900">Part numbers</h2>
          <p className="text-[15px] text-slate-600">Pricing rules for each SKU included in this opportunity.</p>
        </div>

        {opportunity?.details && opportunity.details.length > 0 ? (
          <div className="mt-6">
            <OpportunityDetailsGrid opportunityDetails={opportunity.details} onChanged={() => opportunityQuery.refetch()} />
          </div>
        ) : (
          <Card className="mt-6 rounded-2xl border border-dashed border-gray-200 bg-white shadow-sm">
            <CardHeader>
              <CardTitle className="text-xl font-medium text-slate-900">No part numbers attached</CardTitle>
              <CardDescription className="text-[15px] text-slate-600">
                Search for a product, open Actions, and add it to this opportunity.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Link
                to={Path.search(applicationId)}
                className="inline-flex items-center gap-2 text-[15px] font-semibold text-[color:var(--ops-accent)] hover:underline"
              >
                Search products
                <ArrowLeft className="h-4 w-4 rotate-180" aria-hidden="true" />
              </Link>
            </CardContent>
          </Card>
        )}
      </section>
    </div>
  );
};

export default withRoot(OpportunityPage);
